import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { api, query } from '../../lib/api';
import type { Appointment, Slot, Therapist, VisitMode } from '../../lib/types';
import { MODE_LABELS, addDays, clinicDate, formatDateTime, formatDay, formatTime, groupByClinicDay } from '../../lib/format';
import { Dialog, ErrorMessage, Loading, PageTitle, SelectField } from '../../components/ui';
import { Pii } from '../../components/Pii';

const DAYS_PER_PAGE = 7;

/**
 * Booking flow: service → therapist (or any) → free slot → confirmation. Links from the website widget
 * (/rezerwacja?slot=…&therapist=…&service=…) open directly at the confirmation step.
 */
export function BookingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const therapists = useQuery({ queryKey: ['therapists'], queryFn: () => api.get<Therapist[]>('/api/v1/therapists') });

  const [serviceId, setServiceId] = useState(params.get('service') ?? '');
  const [therapistId, setTherapistId] = useState(params.get('therapist') ?? '');
  const [weekStart, setWeekStart] = useState(() => addDays(clinicDate(new Date()), 1));
  const [mode, setMode] = useState<VisitMode>((params.get('mode') as VisitMode | null) ?? 'InPerson');
  const preselected = params.get('slot');
  const preselectedTherapist = params.get('therapist');
  const [chosen, setChosen] = useState<Slot | null>(
    preselected && preselectedTherapist ? { startsAt: preselected, endsAt: preselected, therapistId: preselectedTherapist } : null,
  );

  const services = useMemo(() => {
    const map = new Map<string, Therapist['services'][number]>();
    therapists.data?.forEach((t) => t.services.forEach((s) => map.set(s.id, s)));
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }, [therapists.data]);
  const service = services.find((s) => s.id === serviceId);
  const offering = therapists.data?.filter((t) => t.services.some((s) => s.id === serviceId)) ?? [];
  const therapistName = (id: string) => {
    const t = therapists.data?.find((x) => x.id === id);
    return t ? `${t.firstName} ${t.lastName}` : '';
  };

  const slots = useQuery({
    queryKey: ['slots', serviceId, therapistId, weekStart],
    queryFn: () =>
      api.get<Slot[]>(
        `/api/v1/slots${query({ serviceId, therapistId: therapistId || undefined, from: weekStart, to: addDays(weekStart, DAYS_PER_PAGE - 1) })}`,
      ),
    enabled: !!serviceId,
  });

  const book = useMutation({
    mutationFn: (slot: Slot) =>
      api.post<Appointment>('/api/v1/appointments', { therapistId: slot.therapistId, serviceId, startsAt: slot.startsAt, mode }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['appointments'] });
      navigate('/wizyty?zarezerwowano=1');
    },
    onError: () => void client.invalidateQueries({ queryKey: ['slots'] }),
  });

  if (!therapists.data) return <Loading />;
  const modes = service?.modes ?? [];

  return (
    <>
      <PageTitle>Umów wizytę</PageTitle>
      <div className="booking-filters">
        <SelectField
          label="Rodzaj wizyty"
          value={serviceId}
          onChange={(v) => {
            setServiceId(v);
            setTherapistId('');
          }}
          options={[{ value: '', label: 'Wybierz…' }, ...services.map((s) => ({ value: s.id, label: `${s.name} (${s.durationMinutes} min)` }))]}
        />
        {serviceId && (
          <SelectField pii
            label="Terapeuta"
            value={therapistId}
            onChange={setTherapistId}
            options={[{ value: '', label: 'Dowolny terapeuta' }, ...offering.map((t) => ({ value: t.id, label: `${t.firstName} ${t.lastName}` }))]}
          />
        )}
        {modes.length > 1 && (
          <fieldset className="radio-group">
            <legend>Forma wizyty</legend>
            {modes.map((m) => (
              <label key={m}>
                <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} /> {MODE_LABELS[m]}
              </label>
            ))}
          </fieldset>
        )}
      </div>

      {serviceId && (
        <section aria-labelledby="slots-title">
          <div className="week-nav">
            <button type="button" className="secondary" onClick={() => setWeekStart(addDays(weekStart, -DAYS_PER_PAGE))} disabled={weekStart <= clinicDate(new Date())}>
              ← Poprzedni tydzień
            </button>
            <h2 id="slots-title">
              {formatDay(`${weekStart}T12:00:00Z`)} – {formatDay(`${addDays(weekStart, DAYS_PER_PAGE - 1)}T12:00:00Z`)}
            </h2>
            <button type="button" className="secondary" onClick={() => setWeekStart(addDays(weekStart, DAYS_PER_PAGE))}>
              Następny tydzień →
            </button>
          </div>
          <ErrorMessage error={slots.error} />
          {slots.isPending && <Loading label="Szukamy wolnych terminów…" />}
          {slots.data && slots.data.length === 0 && <p>Brak wolnych terminów w tym tygodniu. Sprawdź kolejny tydzień.</p>}
          {slots.data && (
            <div className="slot-days">
              {groupByClinicDay(slots.data).map(([day, daySlots]) => (
                <div key={day} className="slot-day">
                  <h3>{formatDay(`${day}T12:00:00Z`)}</h3>
                  <ul className="slot-list">
                    {daySlots.map((slot) => (
                      <li key={`${slot.therapistId}-${slot.startsAt}`}>
                        <button type="button" className="slot" onClick={() => setChosen(slot)}>
                          {formatTime(slot.startsAt)}
                          {!therapistId && (
                            <span className="slot-therapist">
                              <Pii>{therapistName(slot.therapistId)}</Pii>
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <Dialog open={!!chosen} title="Potwierdź rezerwację" onClose={() => setChosen(null)}>
        {chosen && (
          <>
            <dl className="summary">
              <dt>Termin</dt>
              <dd>{formatDateTime(chosen.startsAt)}</dd>
              <dt>Terapeuta</dt>
              <dd>
                <Pii>{therapistName(chosen.therapistId)}</Pii>
              </dd>
              <dt>Wizyta</dt>
              <dd>{service?.name ?? '—'}</dd>
              <dt>Forma</dt>
              <dd>{MODE_LABELS[mode]}</dd>
            </dl>
            {!serviceId && <p>Wybierz rodzaj wizyty, aby dokończyć rezerwację.</p>}
            <ErrorMessage error={book.error} />
            <div className="button-row">
              <button type="button" disabled={!serviceId || book.isPending} onClick={() => book.mutate(chosen)}>
                Rezerwuję
              </button>
              <button type="button" className="secondary" onClick={() => setChosen(null)}>
                Anuluj
              </button>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
}
